import { Directive, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Subscription } from 'rxjs';
import { ProfileService } from '../../services/profile.service';
import {
  LocationOption,
  locationToggle,
  locationApplyResult,
  locationCancelRestoreTemp
} from '../../shared/utils/location-dropdown.util';

export type { LocationOption };

/**
 * Abstract base class shared by CampaignToolbarComponent, SegmentsToolbarComponent, and AppointmentsToolbarComponent.
 * Contains the location-selection state and all location-management methods.
 */
@Directive()
export abstract class BaseToolbarComponent implements OnInit, OnDestroy {
  @Input() selectedLocation = '';
  @Input() searchPlaceholder = 'Search...';

  @Output() searchChanged = new EventEmitter<string>();
  @Output() locationChanged = new EventEmitter<string[]>();
  @Output() locationSelectionFromModal = new EventEmitter<string[]>();

  locations: LocationOption[] = [];
  tempSelectedLocations = new Set<string>();
  selectedLocationLabels: string[] = [];
  searchTerm = '';
  isLocationDropdownOpen = false;

  /** When true, cancel restores temp from labels (e.g. AppointmentsToolbarComponent). Default false. */
  protected restoreTempOnCancel = false;

  protected locationsSubscription: Subscription = new Subscription();

  constructor(protected profileService: ProfileService) {}

  ngOnInit(): void {
    this.loadLocations();
  }

  ngOnDestroy(): void {
    this.locationsSubscription.unsubscribe();
  }

  /**
   * Loads locations via ProfileService, maps API locations to selectable options, stores the subscription, and triggers onLocationsLoaded.
   * @example
   * loadLocations()
   * void
   * @returns {void} Does not return a value; updates component state and manages side effects via subscriptions.
   */
  protected loadLocations(): void {
    this.profileService.loadLocations();
    this.locationsSubscription = this.profileService.locations$.subscribe(
      (apiLocations) => {
        this.locations = apiLocations.map(location => ({
          value: location.id,
          label: location.branch,
          selected: location.id === this.selectedLocation,
        }));
        this.onLocationsLoaded();
      }
    );
  }

  /** Override in subclass to handle post-load defaults (e.g. set a default selected location). */
  protected onLocationsLoaded(): void {}

  onSearchChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.searchTerm = target.value;
    this.searchChanged.emit(this.searchTerm);
  }

  clearSearch(): void {
    this.searchTerm = '';
    this.searchChanged.emit(this.searchTerm);
  }

  /**
  * Toggle the location dropdown and, when opening, clear and populate temporary selected locations from the current selected labels.
  * @example
  * oggleLocationDropdown()
  * undefined
  * @returns {void} Toggles isLocationDropdownOpen and, if opened, clears tempSelectedLocations then adds location values matching selectedLocationLabels.
  **/
  toggleLocationDropdown(): void {
    this.isLocationDropdownOpen = !this.isLocationDropdownOpen;
    if (this.isLocationDropdownOpen) {
      this.tempSelectedLocations.clear();
      this.selectedLocationLabels.forEach(label => {
        const location = this.locations.find(loc => loc.label === label);
        if (location) {
          this.tempSelectedLocations.add(location.value);
        }
      });
    }
  }

  onLocationToggle(locationValue: string): void {
    locationToggle(this.tempSelectedLocations, locationValue);
  }

  onLocationApply(): void {
    const { selectedLocation, selectedLocationLabels } = locationApplyResult(
      this.tempSelectedLocations,
      this.locations
    );
    this.selectedLocation = selectedLocation;
    this.selectedLocationLabels = selectedLocationLabels;
    this.locationChanged.emit(this.selectedLocationLabels);
    this.isLocationDropdownOpen = false;
  }

  onLocationCancel(): void {
    this.tempSelectedLocations = locationCancelRestoreTemp(
      this.selectedLocation,
      this.locations,
      this.restoreTempOnCancel
    );
    this.isLocationDropdownOpen = false;
  }

  onLocationClear(): void {
    this.tempSelectedLocations.clear();
    this.selectedLocation = '';
    this.selectedLocationLabels = [];
    this.locationChanged.emit([]);
  }

  isLocationSelected(locationValue: string): boolean {
    return this.tempSelectedLocations.has(locationValue);
  }

  getSelectedLocationLabel(): string {
    if (this.tempSelectedLocations.size === 0) {
      return 'Select Location';
    }
    if (this.tempSelectedLocations.size === 1) {
      const location = this.locations.find((loc) => loc.value === Array.from(this.tempSelectedLocations)[0]);
      return location ? location.label : 'Select Location';
    }
    return `${this.tempSelectedLocations.size} locations`;
  }

  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.location-dropdown')) {
      this.isLocationDropdownOpen = false;
    }
  }

  onLocationSelectionFromModal(locationLabels: string[]): void {
    this.selectedLocationLabels = locationLabels;
    this.tempSelectedLocations.clear();
    locationLabels.forEach(label => {
      const location = this.locations.find(loc => loc.label === label);
      if (location) {
        this.tempSelectedLocations.add(location.value);
      }
    });
    this.locationSelectionFromModal.emit(locationLabels);
  }
}
